import { useCallback, useSyncExternalStore } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { askMoo } from '../../../queries/ask-moo';
import {
  createAgent,
  updateAgent,
  deleteAgent,
  publishAgent,
  duplicateAgent,
  grantAgentAccess,
  revokeAgentAccess,
} from '../api';
import { getData } from '../../../utils/functional/fp';
import { useAuth } from '../../hooks/useAuth';
import type { Agent } from '../types';

export function useAgentsList(scope?: string, options?: { enabled?: boolean }) {
  // Key the catalog by the account type the user is acting in so an in-app
  // account switch refetches instead of serving the previous account's
  // (differently-gated) catalog. `scope` (OMNI-796) is a separate cache key
  // and GET, not a client-side filter — see askMoo.agents.
  const { accountType } = useAuth();
  return useQuery({
    ...askMoo.agents(accountType ?? undefined, scope),
    enabled: options?.enabled,
  });
}

export function useAgent(agentId: string) {
  return useQuery({
    ...askMoo.agent(agentId),
    enabled: !!agentId,
  });
}

export function useStarters(
  agentId: string,
  params?: {
    view?: string;
    subTab?: string;
    entityType?: string;
    entityId?: string;
    entityName?: string;
  }
) {
  return useQuery({
    ...askMoo.starters(agentId, params),
    enabled: !!agentId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useCreateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentData: Partial<Agent>) =>
      createAgent({ data: agentData }).then(getData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: askMoo.agents._def });
    },
  });
}

export function useUpdateAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agentId,
      data,
    }: {
      agentId: string;
      data: Partial<Agent>;
    }) => updateAgent(agentId, { data }).then(getData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: askMoo.agents._def });
    },
  });
}

export function useDeleteAgent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentId: string) => deleteAgent(agentId).then(getData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: askMoo.agents._def });
    },
  });
}

const FAVORITES_KEY = 'moo-agent-favorites';

// Shared in-memory snapshot — all hook instances in the same tab read from this.
let _snapshot: Set<string> = readFromStorage();
const _listeners = new Set<() => void>();

function readFromStorage(): Set<string> {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function emitChange() {
  _listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  _listeners.add(listener);

  // Cross-tab sync via StorageEvent
  const onStorage = (e: StorageEvent) => {
    if (e.key === FAVORITES_KEY) {
      _snapshot = readFromStorage();
      emitChange();
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    _listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

function getSnapshot() {
  return _snapshot;
}

function toggleFavorite(agentId: string) {
  const next = new Set(_snapshot);
  if (next.has(agentId)) next.delete(agentId);
  else next.add(agentId);
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(Array.from(next)));
  _snapshot = next;
  emitChange();
}

export function useAgentFavorites() {
  const favs = useSyncExternalStore(subscribe, getSnapshot);

  const isFavorite = useCallback(
    (agentId: string) => favs.has(agentId),
    [favs]
  );

  const toggle = useCallback((agentId: string) => {
    toggleFavorite(agentId);
  }, []);

  return { isFavorite, toggle };
}

/** @deprecated Use useAgentFavorites() instead */
export function useToggleAgentFavorite() {
  const { toggle } = useAgentFavorites();
  return { mutate: toggle, isPending: false };
}

export function usePublishAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => publishAgent(agentId).then(getData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: askMoo.agents._def });
    },
  });
}

export function useDuplicateAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (agentId: string) => duplicateAgent(agentId).then(getData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: askMoo.agents._def });
    },
  });
}

export function useAgentAccess(agentId: string) {
  return useQuery({
    ...askMoo.agentAccess(agentId),
    enabled: !!agentId,
  });
}

export function useGrantAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      agentId,
      data,
    }: {
      agentId: string;
      data: { userId: string; userName: string; userEmail: string };
    }) => grantAgentAccess(agentId, { data }).then(getData),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: askMoo.agentAccess(variables.agentId).queryKey,
      });
      queryClient.invalidateQueries({ queryKey: askMoo.agents._def });
    },
  });
}

export function useRevokeAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      agentId,
      accessId,
    }: {
      agentId: string;
      accessId: string;
    }) => revokeAgentAccess(agentId, accessId).then(getData),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: askMoo.agentAccess(variables.agentId).queryKey,
      });
      queryClient.invalidateQueries({ queryKey: askMoo.agents._def });
    },
  });
}

export function useUserSearch(query: string) {
  return useQuery({
    ...askMoo.userSearch(query),
    enabled: query.length >= 2,
    staleTime: 60 * 1000,
  });
}

/**
 * Users in the caller's account for the dwell-audience picker (OMNI-757).
 * Always enabled — the picker must be browsable from an empty box, so there is
 * no minimum query length here.
 */
export function useAccountUsers(search: string) {
  return useQuery({
    ...askMoo.accountUsers(search),
    staleTime: 60 * 1000,
  });
}
